/**
 * In-App Notifications Service
 *
 * Handles fetching, caching, and managing in-app notifications from the backend API.
 * Provides reactive state management for notification data.
 */

import { apiClient } from './api-client';
import { createStore, type Store } from './store';
import {
  notifyPendingApproval,
  notifyRequestApproved,
  notifyRequestRejected,
  notifyCommentAdded,
  notifyRequestSubmitted,
  notifyRequestCancelled,
  selectCanShowNotifications,
  getBrowserNotificationsState,
} from './browser-notifications';

/**
 * Notification type from backend
 */
export type NotificationType =
  | 'REQUEST_SUBMITTED'
  | 'REQUEST_APPROVED'
  | 'REQUEST_REJECTED'
  | 'REQUEST_CANCELLED'
  | 'APPROVAL_REQUIRED'
  | 'COMMENT_ADDED'
  | 'DELEGATION_ASSIGNED'
  | 'DELEGATION_ENDED';

/**
 * Notification entity from backend
 */
export interface InAppNotification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  request_id: string | null;
  actor_id: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
}

/**
 * Pagination metadata
 */
export interface NotificationPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

/**
 * Notifications list response from API
 */
interface NotificationsListResponse {
  notifications: InAppNotification[];
  pagination: NotificationPagination;
  unreadCount: number;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  data: T;
  message: string;
}

/**
 * In-App Notifications store state
 */
export interface InAppNotificationsState {
  notifications: InAppNotification[];
  unreadCount: number;
  pagination: NotificationPagination | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
}

/**
 * Initial state
 */
const initialState: InAppNotificationsState = {
  notifications: [],
  unreadCount: 0,
  pagination: null,
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * In-App Notifications store
 */
const inAppNotificationsStore: Store<InAppNotificationsState> = createStore(initialState);

/**
 * Set of known notification IDs to detect new notifications
 */
const knownNotificationIds = new Set<string>();

/**
 * Flag to track if initial fetch has completed
 * We don't want to spam browser notifications for existing notifications on page load
 */
let initialFetchComplete = false;

/**
 * Trigger browser notification for a new in-app notification
 */
function triggerBrowserNotification(notification: InAppNotification): void {
  // Only show browser notifications if permission is granted and enabled
  const browserState = getBrowserNotificationsState();
  if (!selectCanShowNotifications(browserState)) {
    return;
  }

  // Only show for unread notifications
  if (notification.is_read) {
    return;
  }

  const requestId = notification.request_id || '';
  const title = notification.title || 'Notification';

  switch (notification.type) {
    case 'APPROVAL_REQUIRED':
    case 'REQUEST_SUBMITTED':
      // For approval requests, show pending approval notification
      notifyPendingApproval(
        requestId,
        title,
        notification.message.split(' ')[0] || 'Someone' // Extract name from message
      );
      break;

    case 'REQUEST_APPROVED':
      notifyRequestApproved(
        requestId,
        title,
        notification.message.split(' ')[0] || 'Approver',
        notification.message.toLowerCase().includes('fully approved')
      );
      break;

    case 'REQUEST_REJECTED':
      notifyRequestRejected(
        requestId,
        title,
        notification.message.split(' ')[0] || 'Approver'
      );
      break;

    case 'COMMENT_ADDED':
      notifyCommentAdded(
        requestId,
        title,
        notification.message.split(' ')[0] || 'Someone',
        notification.message
      );
      break;

    case 'REQUEST_CANCELLED':
      notifyRequestCancelled(
        requestId,
        title,
        notification.message.split(' ')[0] || 'Someone'
      );
      break;

    default:
      // For other notification types, use a generic notification
      notifyRequestSubmitted(
        requestId,
        title,
        notification.message.split(' ')[0] || 'Someone',
        0,
        ''
      );
      break;
  }
}

/**
 * Check for new notifications and trigger browser notifications
 */
function checkForNewNotifications(notifications: InAppNotification[]): void {
  // On the first fetch, just populate known IDs without triggering browser notifications
  // This prevents spamming users with notifications for old messages on page load
  if (!initialFetchComplete) {
    for (const notification of notifications) {
      knownNotificationIds.add(notification.id);
    }
    initialFetchComplete = true;
    return;
  }

  // For subsequent fetches, check for new notifications
  for (const notification of notifications) {
    // If we haven't seen this notification before and it's unread
    if (!knownNotificationIds.has(notification.id) && !notification.is_read) {
      // Trigger browser notification
      triggerBrowserNotification(notification);
    }
    // Add to known IDs
    knownNotificationIds.add(notification.id);
  }
}

/**
 * Fetch notifications from the API
 */
export async function fetchNotifications(options?: {
  page?: number;
  limit?: number;
  isRead?: boolean;
}): Promise<void> {
  const state = inAppNotificationsStore.getState();

  // Prevent concurrent fetches
  if (state.isLoading) {
    return;
  }

  inAppNotificationsStore.setState({ isLoading: true, error: null });

  try {
    const params: Record<string, string | number | boolean | undefined> = {};
    if (options?.page !== undefined) params.page = options.page;
    if (options?.limit !== undefined) params.limit = options.limit;
    if (options?.isRead !== undefined) params.is_read = options.isRead;

    const response = await apiClient.get<ApiResponse<NotificationsListResponse>>(
      '/notifications',
      { params }
    );

    const data = response.data.data;

    // Check for new notifications and trigger browser notifications
    checkForNewNotifications(data.notifications);

    inAppNotificationsStore.setState({
      notifications: data.notifications,
      unreadCount: data.unreadCount,
      pagination: data.pagination,
      isLoading: false,
      lastFetched: Date.now(),
    });
  } catch (error) {
    console.error('Failed to fetch notifications:', error);
    inAppNotificationsStore.setState({
      isLoading: false,
      error: error instanceof Error ? error.message : 'Failed to fetch notifications',
    });
  }
}

/**
 * Fetch just the unread count
 */
export async function fetchUnreadCount(): Promise<number> {
  try {
    const response = await apiClient.get<ApiResponse<{ unreadCount: number }>>(
      '/notifications/unread-count'
    );
    const unreadCount = response.data.data.unreadCount;

    inAppNotificationsStore.setState({ unreadCount });

    return unreadCount;
  } catch (error) {
    console.error('Failed to fetch unread count:', error);
    return inAppNotificationsStore.getState().unreadCount;
  }
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: string): Promise<void> {
  try {
    await apiClient.put<ApiResponse<InAppNotification>>(
      `/notifications/${notificationId}/read`
    );

    // Update local state
    const state = inAppNotificationsStore.getState();
    const updatedNotifications = state.notifications.map((n) =>
      n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
    );
    const wasUnread = state.notifications.find((n) => n.id === notificationId && !n.is_read);

    inAppNotificationsStore.setState({
      notifications: updatedNotifications,
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    });
  } catch (error) {
    console.error('Failed to mark notification as read:', error);
    throw error;
  }
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<void> {
  try {
    await apiClient.put<ApiResponse<{ markedCount: number }>>('/notifications/read-all');

    // Update local state
    const state = inAppNotificationsStore.getState();
    const updatedNotifications = state.notifications.map((n) => ({
      ...n,
      is_read: true,
      read_at: n.read_at || new Date().toISOString(),
    }));

    inAppNotificationsStore.setState({
      notifications: updatedNotifications,
      unreadCount: 0,
    });
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
    throw error;
  }
}

/**
 * Delete a notification
 */
export async function deleteNotification(notificationId: string): Promise<void> {
  try {
    await apiClient.delete(`/notifications/${notificationId}`);

    // Update local state
    const state = inAppNotificationsStore.getState();
    const notification = state.notifications.find((n) => n.id === notificationId);
    const wasUnread = notification && !notification.is_read;

    inAppNotificationsStore.setState({
      notifications: state.notifications.filter((n) => n.id !== notificationId),
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    });
  } catch (error) {
    console.error('Failed to delete notification:', error);
    throw error;
  }
}

/**
 * Get current state
 */
export function getInAppNotificationsState(): Readonly<InAppNotificationsState> {
  return inAppNotificationsStore.getState();
}

/**
 * Subscribe to state changes
 */
export const subscribeToInAppNotifications = inAppNotificationsStore.subscribe.bind(
  inAppNotificationsStore
);

/**
 * Selectors
 */
export const selectNotifications = (state: InAppNotificationsState): InAppNotification[] =>
  state.notifications;

export const selectUnreadCount = (state: InAppNotificationsState): number => state.unreadCount;

export const selectIsLoading = (state: InAppNotificationsState): boolean => state.isLoading;

export const selectError = (state: InAppNotificationsState): string | null => state.error;

export const selectUnreadNotifications = (state: InAppNotificationsState): InAppNotification[] =>
  state.notifications.filter((n) => !n.is_read);

/**
 * Polling configuration
 */
let pollingInterval: ReturnType<typeof setInterval> | null = null;
const POLLING_INTERVAL_MS = 30000; // 30 seconds

/**
 * Start polling for new notifications
 */
export function startPolling(): void {
  if (pollingInterval) {
    return;
  }

  // Initial fetch
  fetchNotifications().catch(console.error);

  // Set up polling - fetch full notifications to trigger browser notifications for new ones
  pollingInterval = setInterval(() => {
    fetchNotifications().catch(console.error);
  }, POLLING_INTERVAL_MS);
}

/**
 * Stop polling for notifications
 */
export function stopPolling(): void {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}

/**
 * Reset store to initial state
 */
export function resetInAppNotificationsState(): void {
  stopPolling();
  inAppNotificationsStore.setState(initialState);
  // Clear known notification IDs and reset initial fetch flag
  knownNotificationIds.clear();
  initialFetchComplete = false;
}

export { inAppNotificationsStore };
